import React, { useState, useEffect } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectBalances } from '@stripe/react-connect-js';
import { createAccountSession, getConnectedAccount } from '../../api/stripeConnect';
import config from '../../config';
// import { loadStripe } from '@stripe/stripe-js';
// import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
// import PaymentForm from './PaymentForm';






interface ReportingChartProps {
  communityId?: string;
  userId?: string;
  onClose?: () => void;
  reportName?: 'net_volume' | 'gross_volume';
  intervalStart?: Date;
  intervalEnd?: Date;
  intervalType?: 'day' | 'month' | 'quarter' | 'year';
}

const ReportingChart: React.FC<ReportingChartProps> = ({ 
  communityId, 
  userId, 
  onClose,
  reportName = 'net_volume',
  intervalStart,
  intervalEnd,
  intervalType = 'day'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);

  useEffect(() => {
    const initializePayments = async () => {
      // Guard: only proceed in secure contexts
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        setError('Payment features require a secure context (HTTPS or localhost).');
        setIsLoading(false);
        return;
      }

      // If communityId and userId are provided, use them to get the account
      if (communityId && userId) {
        try {
          // Get the connected account for this community and user
          const accountData = await getConnectedAccount(communityId, userId);
          if (!accountData || !accountData.accountId) {
            setError('No connected account found for this community and user');
            setIsLoading(false);
            return;
          }

          // setAccountId(accountData.accountId);

          // Create AccountSession with reporting_chart component enabled
          const sessionData = await createAccountSession({
            accountId: accountData.accountId,
            components: {
              reporting_chart: { enabled: true },
            },
          });

          // Render only if the component is enabled in the session
          const isEnabled = sessionData?.components?.reporting_chart?.enabled === true;
          if (!isEnabled) {
            // setError('Balances/reporting is not enabled for this account.');
            setIsLoading(false);
            return;
          }

          // setClientSecret(sessionData.client_secret);

          // Determine publishable key
          const publishableKey = config.stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
          if (!publishableKey) {
            setError('Stripe publishable key is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment.');
            setIsLoading(false);
            return;
          }

          // Initialize Connect.js
          const connectInstance = await loadConnectAndInitialize({
            publishableKey,
            fetchClientSecret: async () => {
              const fresh = await createAccountSession({
                accountId: accountData.accountId,
                components: {
                  reporting_chart: { enabled: true },
                },
              });
              return fresh.client_secret;
            },
            appearance: {
              variables: {
                colorPrimary: '#3b82f6',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '8px',
                borderRadius: '8px',
              },
            },
          });

          setStripeConnectInstance(connectInstance);

          setIsLoading(false);
        } catch (err) {
          console.error('Error initializing payments:', err);
          
          // Handle different types of errors
          if (err instanceof Error) {
            if (err.message.includes('Account ID')) {
              setError('Invalid account configuration. Please contact support.');
            } else if (err.message.includes('Failed to create')) {
              setError('Unable to create payment session. Please try again.');
            } else if (err.message.includes('network')) {
              setError('Network error. Please check your connection and try again.');
            } else {
              setError(err.message);
            }
          } else {
            setError('An unexpected error occurred. Please try again.');
          }
          setIsLoading(false);
        }
      } else {
        // If no communityId/userId provided, show error
        setError('Community ID and User ID are required to access payment dashboard');
        setIsLoading(false);
      }
    };

    initializePayments();

    // Cleanup function
    return () => {
      setStripeConnectInstance(null);
    };
  }, [communityId, userId, reportName, intervalStart, intervalEnd, intervalType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reporting chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Reporting Chart</h3>
            {/* <p className="text-sm text-red-700 mt-1">{error}</p> */}
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="mt-4 bg-red-100 text-red-800 px-4 py-2 rounded hover:bg-red-200"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="">
      <div className="flex items-center justify-between">
        {/* <h2 className="text-xl font-semibold text-gray-900">Reporting Chart Dashboard</h2> */}
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      <div className="bg-white border rounded-lg overflow-hidden ">
        <div className="p-4 border-b bg-gray-50">
          <p className="text-sm text-gray-600">
            View Payment Dashboard charts such as Net Volume and Gross Volume charts.
          </p>
        </div>
        
        {/* Stripe Connect Balances/Reporting via React components */}
        <div className="min-h-[600px] px-4 py-2">
          {stripeConnectInstance && (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <ConnectBalances />
            </ConnectComponentsProvider>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Available Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>View Net Volume charts</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>View Gross Volume charts</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Customizable date ranges</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Multiple interval types (day, month, quarter, year)</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Interactive chart visualization</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            <span>Real-time data updates</span>
          </div>
        </div>
      </div>
    </div>
  );
};






export default ReportingChart; 